import { useFocusTrap } from "../hooks/useFocusTrap";
import { useBriefSurface } from "../hooks/useBriefSurface";
import { useEffect, useMemo, useState } from "react";
import { DialogClose } from "./DialogClose";
import type { MarineSnapshot } from "./CoastalBrief";
import type { TideSnapshot } from "./TidePanel";
import {
  englishText,
  feedHealthSentence,
  ictTime,
  LEVEL_LABEL,
  lineText,
  type IncidentBriefFeed,
  thaiDate,
  type AirBriefInput,
  type BriefTile,
  type FeedHealthInput,
} from "../lib/mayorBrief";
import type { PrecipNowcast, ReservoirObservation } from "@chonburi/shared";
import { ageClass } from "../lib/panelHeader";
import type { LensId } from "../map/presets";

/**
 * Mayor brief — one screen the mayor of Chonburi can read in 30 seconds and
 * forward to a LINE group.
 *
 * The semantics live in lib/mayorBrief.ts; this file is the surface. Thai is
 * written first and English sits directly beneath it, because the brief is
 * forwarded inside Thailand and read outside it — so both are first-class
 * sentences rather than one being a translation of the other.
 *
 * Every tile keeps its source and reference time. A feed that is missing
 * says "no data" on the tile; it never reads as calm.
 */

const PUBLIC_URL = "https://chonburi.nonarkara.org";

interface FeedMeta {
  ageMinutes: number | null;
  loadedAt: string | null;
  fallbackTier: string;
  source: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Jump to the lens where a tile's underlying evidence lives. */
  onOpenLens: (lens: LensId) => void;
  air: AirBriefInput | null;
  rain: PrecipNowcast | null;
  rainMeta: FeedMeta;
  reservoirs: ReservoirObservation[];
  reservoirMeta: FeedMeta;
  marine: MarineSnapshot | null;
  marineMeta: FeedMeta;
  tides: TideSnapshot | null;
  tideMeta: FeedMeta;
  incidentFeeds: IncidentBriefFeed[];
  feeds: FeedHealthInput;
}

const COPY_IDLE = "idle";
type CopyState = typeof COPY_IDLE | "copied" | "failed";

function minutesSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((now.getTime() - t) / 60_000));
}

export function MayorBrief({
  open,
  onClose,
  onOpenLens,
  air,
  rain,
  rainMeta,
  reservoirs,
  reservoirMeta,
  marine,
  marineMeta,
  tides,
  tideMeta,
  incidentFeeds,
  feeds,
}: Props) {
  const containerRef = useFocusTrap(open);
  const [copyState, setCopyState] = useState<CopyState>(COPY_IDLE);
  const [copiedWhich, setCopiedWhich] = useState<"th" | "en">("th");

  // The four tiles, shaped from the raw feeds by the shared surface hook, so
  // the brief and the mayor's desk can never disagree about the same dam.
  const { tiles, actions, now } = useBriefSurface({
    air, rain, rainMeta, reservoirs, reservoirMeta,
    marine, marineMeta, tides, tideMeta, incidentFeeds, feeds,
  });

  useEffect(() => {
    if (copyState !== "copied") return;
    const timer = window.setTimeout(() => setCopyState(COPY_IDLE), 2500);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const brief = useMemo(
    () => ({
      tiles,
      actions,
      health: feedHealthSentence(feeds),
      th: lineText(tiles, actions, now, PUBLIC_URL, feeds),
      en: englishText(tiles, actions, now, PUBLIC_URL, feeds),
    }),
    [tiles, actions, feeds, now],
  );

  useEffect(() => {
    if (!open) return;
    let closed: HTMLDetailsElement[] = [];
    const expand = () => {
      closed = [...(containerRef.current?.querySelectorAll<HTMLDetailsElement>("details:not([open])") ?? [])];
      closed.forEach(detail => { detail.open = true; });
    };
    const restore = () => { closed.forEach(detail => { detail.open = false; }); closed = []; };
    window.addEventListener("beforeprint", expand);
    window.addEventListener("afterprint", restore);
    return () => { restore(); window.removeEventListener("beforeprint", expand); window.removeEventListener("afterprint", restore); };
  }, [open, containerRef]);

  if (!open) return null;

  const copy = async (which: "th" | "en") => {
    setCopiedWhich(which);
    try {
      await navigator.clipboard.writeText(which === "th" ? brief.th : brief.en);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const goToEvidence = (tile: BriefTile) => {
    onClose();
    onOpenLens(tile.evidence as LensId);
  };

  return (
    <div className="modal-backdrop mayor-brief-backdrop" onClick={onClose}>
      <div
        ref={containerRef} className="mayor-brief"
        role="dialog"
        aria-modal="true"
        aria-label="สรุปสำหรับนายกเมือง / Mayor brief"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mayor-brief-head">
          <div className="mayor-brief-identity">
            <h2>สรุปเมืองชลบุรี <span>Chonburi brief</span></h2>
            <p className="mayor-brief-date">{thaiDate(now.toISOString())} · {ictTime(now)} ICT</p>
          </div>
          <div className="mayor-brief-actions">
            <button type="button" onClick={() => copy("th")} className="mayor-brief-primary">
              {copyState === "copied" && copiedWhich === "th" ? "คัดลอกแล้ว · Copied" : "คัดลอกส่ง LINE"}
            </button>
            <button type="button" onClick={() => copy("en")}>
              {copyState === "copied" && copiedWhich === "en" ? "Copied" : "Copy English"}
            </button>
            <button type="button" onClick={() => window.print()}>
              พิมพ์ · Print
            </button>
          </div>
          <DialogClose onClose={onClose} label="ปิดสรุปนายกเมือง / Close mayor brief" />
        </div>

        {/* The status line exists for the copy result, not to explain a
            button. A permanent band here would spend a row of the first
            screen saying something the button and the text below already say. */}
        {copyState !== "idle" && (
          <p role="status" className="mayor-brief-status">
            {copyState === "failed"
              ? "คัดลอกไม่สำเร็จ — เปิดข้อความด้านล่างแล้วเลือกคัดลอกด้วยตนเอง · Copy failed; open the text below and copy it manually."
              : "คัดลอกข้อความพร้อมแหล่งที่มาและเวลาอ้างอิงแล้ว · Copied, with sources and reference times."}
          </p>
        )}

        <div className="mayor-brief-body">
          <section className="mayor-brief-tiles" aria-label="สถานการณ์หลัก / Key conditions">
            {brief.tiles.map((tile) => {
              const age = minutesSince(tile.observedAt, now);
              const label = tile.levelLabel ?? LEVEL_LABEL[tile.level];
              return (
                <article key={tile.key} className={`mayor-tile mayor-tile--${tile.level}`}>
                  <div className="mayor-tile-head">
                    <h3>{tile.titleTh}</h3>
                    <span className="mayor-tile-level mono" title={label.en} aria-label={`${label.th} · ${label.en}`}>
                      {label.th}
                    </span>
                  </div>
                  <p className="mayor-tile-th">{tile.summaryTh ?? tile.th}</p>
                  <p className="mayor-tile-en">{tile.summaryEn ?? tile.en}</p>
                  <div className="mayor-tile-meta">
                    <div className="mayor-tile-provenance">
                      <span className="mono">{tile.source}</span>
                      {age != null ? (
                        <span className={`mono data-age ${ageClass(age)}`} title="Reference time">
                          {new Date(tile.observedAt!).toLocaleString("th-TH", {
                            timeZone: "Asia/Bangkok",
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </span>
                      ) : (
                        <span className="mono data-age data-age--crit">ไม่ทราบเวลาอ้างอิง</span>
                      )}
                    </div>
                    <div className="mayor-tile-tools">
                  <details className="mayor-tile-details">
                    <summary>รายละเอียด · Details</summary>
                    <div className="mayor-tile-full">
                      <h4>{tile.titleEn}</h4>
                      <p lang="th">{tile.th}</p><p lang="en">{tile.en}</p>
                    </div>
                  </details>
                    <button type="button" onClick={() => goToEvidence(tile)} className="mayor-tile-evidence">
                      ดูหลักฐาน · Inspect evidence
                    </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>

          <details className="mayor-brief-section mayor-brief-integrity">
            <summary>ข้อมูลสรุป · Brief inputs {feeds.live}/{feeds.total} live feeds · {(feeds.scenario ?? 0) + feeds.down + (feeds.loading ?? 0)} missing/example</summary>
            <p className="mayor-brief-integrity-line">
              <span>{brief.health.th}</span>
              <span className="mayor-action-en">{brief.health.en}</span>
            </p>
            <p className="mayor-brief-note">
              ตัวเลขในหน้านี้มาจากแหล่งข้อมูลที่ระบุไว้ใต้แต่ละการ์ด และบางค่าเป็นแบบจำลองหรือค่าประมาณ ไม่ใช่การวัดจริง
              ทุกครั้ง · Every figure carries its source below it; some values are models or estimates, not direct measurements.
            </p>
            {feeds.inputs && <ul className="mayor-inputs">{feeds.inputs.map(input => <li key={input.name}>{input.name} · {input.kind} · {input.tier}</li>)}</ul>}
          </details>

          {brief.actions.length > 0 && (
            <section className="mayor-brief-section">
              <h3>ประเด็นที่ควรตรวจสอบต่อ · Points to check next</h3>
              <ol className="mayor-brief-actions-list">
                {brief.actions.map((action) => (
                  <li key={action.id}>
                    <span className="mayor-action-th">{action.th}</span>
                    <span className="mayor-action-en">{action.en}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <details className="mayor-brief-text">
            <summary>ข้อความสำหรับส่ง LINE (ไทย) · Text that will be copied</summary>
            <pre lang="th">{brief.th}</pre>
            <h3>English text</h3>
            <pre lang="en">{brief.en}</pre>
          </details>

          {/* The pack is the document a mayor reads before and after the
              discussion. It is linked here because the brief is the screen
              that is reachable on every device — the manual's trigger is not. */}
          <p className="mayor-brief-pack">
            <a
              href="/docs/chonburi-mayor-pack-2026-10-23.pdf"
              target="_blank"
              rel="noreferrer noopener"
            >
              เอกสารประชุมพร้อมหลักฐาน · Meeting pack with evidence (PDF)
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
